// Signals learning loop — "Needs review" queue. A human rules per ASSERTION:
// each derived claim (is-a-signal, customer, SKUs, order, batch, duplicate)
// renders with its evidence status (green confident / amber disputed) and can
// be confirmed or corrected inline; submitting builds the per-assertion
// payload. Confirm/correct kicks the lesson stage: the entry is written to
// kb_signals (bridge keys pass the never-wrong-metadata guard), a resolution
// lesson is distilled when corrected, and a harness truth-case candidate is
// snapshotted (kanban 21f9555b, review b3f0c88d).

import { useCallback, useEffect, useRef, useState } from 'react';
import { SignalsTabs } from '../components/signals/SignalsTabs';
import {
  signalsReviewApi,
  type AssertionDecision,
  type AssertionVerdict,
  type DecideRequest,
  type LessonStatus,
  type ProposedEntry,
  type ReviewAssertion,
  type ReviewDetail,
  type ReviewListItem,
  type ReviewStatus,
} from '../services/signalsReview';
import styles from './SignalsReviewPage.module.css';

const STATUSES: { key: ReviewStatus; label: string }[] = [
  { key: 'pending', label: 'Pending' },
  { key: 'confirmed', label: 'Confirmed' },
  { key: 'corrected', label: 'Corrected' },
  { key: 'rejected', label: 'Rejected' },
];

const ENTRY_TYPE_LABEL: Record<string, string> = {
  needs_human_signal: 'needs human',
  skip_audit: 'skip audit',
};

const LESSON_LABEL: Record<LessonStatus, string> = {
  not_started: 'lesson queued',
  running: 'lesson running…',
  written: 'lesson written',
  failed: 'lesson failed',
};

// Why the row is (still) in front of a human — server-derived reason chip.
const REASON_LABEL: Record<string, string> = {
  conflict: 'conflicting evidence',
  possible_duplicate: 'possible duplicate',
  customer_unresolved: 'customer?',
  customer_ambiguous: 'customer ambiguous',
  demoted_bind: 'unverified key dropped',
  ambiguous_keys: 'minor keys ambiguous',
  other: 'review',
};

const REASON_URGENT = new Set(['conflict', 'customer_unresolved', 'customer_ambiguous']);

// Assertions that exist only in the audit trail — correcting them cannot be
// expressed in the entry (is_signal: reject the item instead).
const NON_CORRECTABLE = new Set(['is_signal']);
const ENTRY_EXTERNAL = new Set(['is_signal', 'duplicate_of']);
const SKU_PREFIX = 'sku:';

function age(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(ms)) return '—';
  const mins = Math.floor(ms / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 48) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

interface AssertionState {
  verdict: AssertionVerdict;
  correctedValue: string;
}

function initialAssertionStates(
  assertions: ReviewAssertion[],
): Record<string, AssertionState> {
  const states: Record<string, AssertionState> = {};
  for (const a of assertions) {
    states[a.id] = {
      verdict: 'confirm',
      correctedValue: typeof a.claimed_value === 'string' ? a.claimed_value : '',
    };
  }
  return states;
}

function buildAssertionsPayload(
  assertions: ReviewAssertion[],
  states: Record<string, AssertionState>,
): Record<string, AssertionDecision> {
  const payload: Record<string, AssertionDecision> = {};
  for (const a of assertions) {
    const st = states[a.id];
    if (!st) continue;
    payload[a.id] =
      st.verdict === 'correct'
        ? { verdict: 'correct', corrected_value: st.correctedValue.trim() || null }
        : { verdict: 'confirm' };
  }
  return payload;
}

// Apply the per-assertion corrections to the proposed entry so the
// corrected_entry REFLECTS every ruling (the API validates consistency).
function applyCorrections(
  base: ProposedEntry,
  states: Record<string, AssertionState>,
  observation: string,
  note: string,
): ProposedEntry {
  const bridge = { ...(base.bridge_keys ?? {}) };
  let skus = [...(bridge.sku ?? [])];
  for (const [id, st] of Object.entries(states)) {
    if (st.verdict !== 'correct' || ENTRY_EXTERNAL.has(id)) continue;
    const value = st.correctedValue.trim();
    if (id.startsWith(SKU_PREFIX)) {
      const old = id.slice(SKU_PREFIX.length).toUpperCase();
      skus = skus.filter((s) => s.toUpperCase() !== old);
      if (value && !skus.some((s) => s.toUpperCase() === value.toUpperCase())) {
        skus.push(value);
      }
    } else {
      (bridge as Record<string, unknown>)[id] = value || null;
    }
  }
  return {
    ...base,
    observation: observation.trim() || base.observation,
    note: note.trim(),
    bridge_keys: { ...bridge, sku: skus },
  };
}

export function SignalsReviewPage() {
  const [status, setStatus] = useState<ReviewStatus>('pending');
  const [items, setItems] = useState<ReviewListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await signalsReviewApi.list(status);
      setItems(res.items);
      setTotal(res.total);
    } catch {
      setError('Could not load the review queue.');
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => {
    void load();
  }, [load]);

  // Pending: lowest-confidence first, so the few genuine conflicts surface
  // above the routine items. Confirmed: auto rulings collapse into their own
  // audit section below the human ones.
  const isAuto = (i: ReviewListItem) => i.decided_by === 'auto';
  const autoItems = status === 'confirmed' ? items.filter(isAuto) : [];
  const visibleItems = (() => {
    let out = status === 'confirmed' ? items.filter((i) => !isAuto(i)) : items;
    if (status === 'pending') {
      out = [...out].sort(
        (a, b) =>
          (a.proposed_entry.confidence ?? 0) - (b.proposed_entry.confidence ?? 0),
      );
    }
    return out;
  })();

  return (
    <div className={styles.page}>
      <SignalsTabs />

      <header className={styles.head}>
        <h1>
          Needs review <span className={styles.beta}>new</span>
        </h1>
        <p>
          Pipeline outcomes awaiting a human ruling. Confirm or correct each
          claim — the ruling writes the signal to kb_signals and teaches the
          resolver; reject archives it.
        </p>
      </header>

      <div className={styles.sliceBar}>
        <span className={styles.sliceLabel}>Status</span>
        <div className={styles.slices}>
          {STATUSES.map((s) => (
            <button
              key={s.key}
              type="button"
              className={status === s.key ? styles.on : ''}
              onClick={() => {
                setStatus(s.key);
                setOpenId(null);
              }}
            >
              {s.label}
            </button>
          ))}
        </div>
        <span className={styles.countNote}>
          {total} item{total === 1 ? '' : 's'}
        </span>
      </div>

      {loading && <div className={styles.muted}>Loading…</div>}
      {error && <div className={styles.error}>{error}</div>}
      {!loading && !error && items.length === 0 && (
        <div className={styles.muted}>
          Nothing {status === 'pending' ? 'awaiting review' : `in ${status}`} —
          the queue fills as the pipeline asks for help.
        </div>
      )}

      {!loading &&
        !error &&
        visibleItems.map((item) => (
          <ReviewCard
            key={item.id}
            item={item}
            open={openId === item.id}
            onToggle={() => setOpenId(openId === item.id ? null : item.id)}
            onDecided={() => void load()}
          />
        ))}

      {!loading && !error && autoItems.length > 0 && (
        <details className={styles.autoSection}>
          <summary>
            Auto-confirmed ({autoItems.length}) — cleared by the confidence
            gate, kept for audit
          </summary>
          {autoItems.map((item) => (
            <ReviewCard
              key={item.id}
              item={item}
              open={openId === item.id}
              onToggle={() => setOpenId(openId === item.id ? null : item.id)}
              onDecided={() => void load()}
            />
          ))}
        </details>
      )}
    </div>
  );
}

function ReviewCard({
  item,
  open,
  onToggle,
  onDecided,
}: {
  item: ReviewListItem;
  open: boolean;
  onToggle: () => void;
  onDecided: () => void;
}) {
  const proposed = item.proposed_entry;
  const customer = proposed.bridge_keys?.customer_no ?? null;
  const excerpt =
    proposed.observation.length > 160
      ? `${proposed.observation.slice(0, 160)}…`
      : proposed.observation;
  return (
    <div className={`${styles.card} ${open ? styles.cardOpen : ''}`}>
      <div
        className={styles.cardHead}
        onClick={onToggle}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === 'Enter' && onToggle()}
      >
        <div className={styles.cardTitleRow}>
          <span
            className={`${styles.typeBadge} ${
              item.entry_type === 'skip_audit' ? styles.typeSkip : ''
            }`}
          >
            {ENTRY_TYPE_LABEL[item.entry_type] ?? item.entry_type}
          </span>
          <span className={styles.subject}>
            {item.email_subject ?? '(no subject)'}
          </span>
          <span className={styles.age}>{age(item.created_at)}</span>
        </div>
        <div className={styles.metaRow}>
          <span>{item.from_address ?? 'unknown sender'}</span>
          <span className={styles.sep}>·</span>
          <span>{item.mailbox}</span>
          {customer && (
            <>
              <span className={styles.sep}>·</span>
              <span className={styles.custChip}>{customer}</span>
            </>
          )}
          {item.status === 'pending' && item.queue_reason && (
            <>
              <span className={styles.sep}>·</span>
              <span
                className={`${styles.reasonChip} ${
                  REASON_URGENT.has(item.queue_reason) ? styles.reasonUrgent : ''
                }`}
                title="Why this needs a human"
              >
                {REASON_LABEL[item.queue_reason] ?? item.queue_reason}
              </span>
            </>
          )}
          {typeof proposed.confidence === 'number' && (
            <>
              <span className={styles.sep}>·</span>
              <span
                className={styles.confChip}
                title="Resolver's calibrated confidence for the binding set"
              >
                conf {proposed.confidence}
              </span>
            </>
          )}
          {item.decided_by === 'auto' && (
            <>
              <span className={styles.sep}>·</span>
              <span className={styles.autoChip}>auto</span>
            </>
          )}
        </div>
        <div className={styles.excerpt}>{excerpt}</div>
      </div>
      {open && <ReviewDetailPanel itemId={item.id} onDecided={onDecided} />}
    </div>
  );
}

function ReviewDetailPanel({
  itemId,
  onDecided,
}: {
  itemId: string;
  onDecided: () => void;
}) {
  const [detail, setDetail] = useState<ReviewDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tracesOpen, setTracesOpen] = useState(false);
  const [states, setStates] = useState<Record<string, AssertionState>>({});
  const [observation, setObservation] = useState('');
  const [note, setNote] = useState('');
  const [rejecting, setRejecting] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [decideError, setDecideError] = useState<string | null>(null);
  const pollTimer = useRef<number | null>(null);

  const fetchDetail = useCallback(async () => {
    try {
      const d = await signalsReviewApi.get(itemId);
      setDetail(d);
      setStates((s) =>
        Object.keys(s).length ? s : initialAssertionStates(d.assertions),
      );
      setObservation((o) => o || d.proposed_entry.observation);
    } catch {
      setError('Could not load the item detail.');
    }
  }, [itemId]);

  useEffect(() => {
    void fetchDetail();
    return () => {
      if (pollTimer.current != null) window.clearTimeout(pollTimer.current);
    };
  }, [fetchDetail]);

  const decide = async (body: DecideRequest) => {
    setBusy(true);
    setDecideError(null);
    try {
      await signalsReviewApi.decide(itemId, body);
      await fetchDetail();
      onDecided();
      // one lesson-status poll shortly after — the Celery lesson usually
      // lands within a few seconds.
      pollTimer.current = window.setTimeout(() => void fetchDetail(), 5000);
    } catch (e: unknown) {
      const detailMsg =
        typeof e === 'object' && e !== null && 'response' in e
          ? String(
              (e as { response?: { data?: { detail?: string } } }).response
                ?.data?.detail ?? '',
            )
          : '';
      setDecideError(detailMsg || 'The decision could not be saved.');
    } finally {
      setBusy(false);
    }
  };

  if (error) return <div className={styles.panelError}>{error}</div>;
  if (!detail) return <div className={styles.panelMuted}>Loading detail…</div>;

  const proposed = detail.proposed_entry;
  const decided = detail.status !== 'pending';
  const anyCorrected = Object.values(states).some(
    (s) => s.verdict === 'correct',
  );

  const setVerdict = (id: string, verdict: AssertionVerdict) =>
    setStates((s) => ({ ...s, [id]: { ...s[id], verdict } }));
  const setCorrectedValue = (id: string, correctedValue: string) =>
    setStates((s) => ({ ...s, [id]: { ...s[id], correctedValue } }));

  const submitReview = () => {
    const assertions = buildAssertionsPayload(detail.assertions, states);
    const payload = Object.keys(assertions).length ? assertions : undefined;
    if (anyCorrected) {
      void decide({
        decision: 'correct',
        corrected_entry: applyCorrections(proposed, states, observation, note),
        assertions: payload,
      });
    } else {
      void decide({ decision: 'confirm', assertions: payload });
    }
  };

  const confirmAll = () => {
    const all = initialAssertionStates(detail.assertions);
    setStates(all);
    const assertions = buildAssertionsPayload(detail.assertions, all);
    void decide({
      decision: 'confirm',
      assertions: Object.keys(assertions).length ? assertions : undefined,
    });
  };

  return (
    <div className={styles.panel}>
      <div className={styles.fieldGrid}>
        <Field label="Observation" wide>
          {proposed.observation}
        </Field>
        {proposed.significance && (
          <Field label="Significance" wide>
            {proposed.significance}
          </Field>
        )}
        <Field label="Themes">{(proposed.themes ?? []).join(', ') || '—'}</Field>
        <Field label="Products">
          {(proposed.products ?? []).join(', ') || '—'}
        </Field>
        <Field label="Observed at">{proposed.observed_at ?? '—'}</Field>
        <Field label="Confidence">{`${proposed.confidence ?? 0}%`}</Field>
        {detail.overturn_category && (
          <Field label="Overturn category" wide>
            {detail.overturn_category}
          </Field>
        )}
      </div>

      <div className={styles.assertionsHead}>
        <span className={styles.assertionsTitle}>
          Assertions ({detail.assertions.length})
        </span>
        {!decided && (
          <div className={styles.headerShortcuts}>
            <button
              type="button"
              className={styles.confirmBtn}
              disabled={busy}
              onClick={confirmAll}
            >
              Confirm all
            </button>
            <button
              type="button"
              className={styles.rejectBtn}
              disabled={busy}
              onClick={() => setRejecting(true)}
            >
              Reject item…
            </button>
          </div>
        )}
      </div>
      <div className={styles.assertionList}>
        {detail.assertions.map((a) => (
          <AssertionRow
            key={a.id}
            assertion={a}
            state={states[a.id]}
            readOnly={decided || busy}
            ruling={detail.human_decision?.assertions?.[a.id] ?? null}
            onVerdict={(v) => setVerdict(a.id, v)}
            onValue={(v) => setCorrectedValue(a.id, v)}
          />
        ))}
        {detail.assertions.length === 0 && (
          <div className={styles.assertionEmpty}>
            No assertions could be derived for this item.
          </div>
        )}
      </div>

      <button
        type="button"
        className={styles.tracesToggle}
        onClick={() => setTracesOpen(!tracesOpen)}
      >
        {tracesOpen ? '▾' : '▸'} Resolution traces (
        {detail.resolution_traces.length})
      </button>
      {tracesOpen && (
        <pre className={styles.traces}>
          {JSON.stringify(detail.resolution_traces, null, 2)}
        </pre>
      )}

      {decided ? (
        <div className={styles.decidedRow}>
          <span className={`${styles.statusChip} ${styles[detail.status]}`}>
            {detail.status}
          </span>
          <span
            className={`${styles.lessonChip} ${
              detail.lesson_status === 'failed' ? styles.lessonFailed : ''
            } ${detail.lesson_status === 'written' ? styles.lessonWritten : ''}`}
          >
            {detail.status === 'rejected'
              ? 'archived, no lesson'
              : LESSON_LABEL[detail.lesson_status]}
          </span>
          {detail.human_decision && (
            <span className={styles.decidedBy}>
              by {detail.human_decision.decided_by}
              {detail.human_decision.reason
                ? ` — ${detail.human_decision.reason}`
                : ''}
            </span>
          )}
          {detail.lesson_error && (
            <div className={styles.panelError}>{detail.lesson_error}</div>
          )}
        </div>
      ) : rejecting ? (
        <div className={styles.form}>
          <label className={styles.formLabel}>
            Reason (required)
            <input
              value={rejectReason}
              placeholder="why this is not a signal"
              onChange={(e) => setRejectReason(e.target.value)}
            />
          </label>
          <div className={styles.actions}>
            <button
              type="button"
              className={styles.rejectBtn}
              disabled={busy || !rejectReason.trim()}
              onClick={() =>
                void decide({ decision: 'reject', reason: rejectReason })
              }
            >
              Reject
            </button>
            <button
              type="button"
              className={styles.cancelBtn}
              disabled={busy}
              onClick={() => setRejecting(false)}
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <div className={styles.form}>
          {anyCorrected && (
            <>
              <label className={styles.formLabel}>
                Observation (edit if the text is wrong too)
                <textarea
                  value={observation}
                  rows={3}
                  onChange={(e) => setObservation(e.target.value)}
                />
              </label>
              <label className={styles.formLabel}>
                Note (anything the fields can&apos;t say)
                <input
                  value={note}
                  placeholder="e.g. sender is a broker for this account"
                  onChange={(e) => setNote(e.target.value)}
                />
              </label>
            </>
          )}
          <div className={styles.actions}>
            <button
              type="button"
              className={anyCorrected ? styles.correctBtn : styles.confirmBtn}
              disabled={busy}
              onClick={submitReview}
            >
              {anyCorrected ? 'Save corrections' : 'Confirm as reviewed'}
            </button>
          </div>
        </div>
      )}
      {decideError && <div className={styles.panelError}>{decideError}</div>}
    </div>
  );
}

function AssertionRow({
  assertion,
  state,
  readOnly,
  ruling,
  onVerdict,
  onValue,
}: {
  assertion: ReviewAssertion;
  state: AssertionState | undefined;
  readOnly: boolean;
  ruling: AssertionDecision | null;
  onVerdict: (v: AssertionVerdict) => void;
  onValue: (v: string) => void;
}) {
  const disputed = assertion.evidence_status === 'disputed';
  const claimed =
    typeof assertion.claimed_value === 'boolean'
      ? assertion.claimed_value
        ? 'yes'
        : 'no'
      : (assertion.claimed_value ?? '—');
  const correcting = state?.verdict === 'correct';
  return (
    <div
      className={`${styles.assertionRow} ${
        disputed ? styles.assertionDisputed : ''
      }`}
    >
      <span
        className={`${styles.evidenceChip} ${
          disputed ? styles.chipDisputed : styles.chipConfident
        }`}
      >
        {assertion.evidence_status}
      </span>
      <div className={styles.assertionBody}>
        <div className={styles.assertionMain}>
          <span className={styles.assertionLabel}>{assertion.label}</span>
          <span className={styles.assertionValue}>{claimed}</span>
          {ruling && (
            <span className={styles.rulingChip}>
              {ruling.verdict === 'correct'
                ? `corrected → ${ruling.corrected_value || 'cleared'}`
                : 'confirmed'}
            </span>
          )}
        </div>
        {disputed && assertion.evidence_note && (
          <div className={styles.evidenceNote}>{assertion.evidence_note}</div>
        )}
        {!readOnly && correcting && (
          <input
            className={styles.assertionInput}
            value={state?.correctedValue ?? ''}
            placeholder="corrected value (empty clears the key)"
            onChange={(e) => onValue(e.target.value)}
          />
        )}
      </div>
      {!readOnly && !ruling && (
        <div className={styles.assertionBtns}>
          <button
            type="button"
            className={!correcting ? styles.miniOn : ''}
            onClick={() => onVerdict('confirm')}
          >
            Confirm
          </button>
          {!NON_CORRECTABLE.has(assertion.id) && (
            <button
              type="button"
              className={correcting ? styles.miniOn : ''}
              onClick={() => onVerdict('correct')}
            >
              Correct
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function Field({
  label,
  children,
  wide,
}: {
  label: string;
  children: React.ReactNode;
  wide?: boolean;
}) {
  return (
    <div className={`${styles.field} ${wide ? styles.fieldWide : ''}`}>
      <div className={styles.fieldLabel}>{label}</div>
      <div className={styles.fieldValue}>{children}</div>
    </div>
  );
}
